import { Link } from "react-router-dom";
import { FaFacebook, FaLinkedin } from "react-icons/fa";
import { SiX, SiMedium } from "react-icons/si";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import QualityNexusLogo from "../QualityNexusLogo";
import { useLanguage } from "@/contexts/LanguageContext";

const Footer = () => {
  const { t } = useLanguage();

  return (
    <footer className="bg-gray-50 dark:dark:bg-nexus-dark/90 pt-16 pb-10">
      <div className="container">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Brand Column */}
          <div className="flex flex-col">
            <div className="flex items-center gap-2 mb-4">
              <QualityNexusLogo className="h-10 w-10" />
              <span className="text-xl font-semibold text-nexus-gray dark:text-gray-200">
                Quality <span className="font-bold">Nexus</span>
              </span>
            </div>
            <p className="text-gray-500 dark:text-gray-300 mb-4">
              {t("footer.about")}
            </p>
            <div className="flex gap-4 text-nexus-gray dark:text-gray-200">
              <a aria-label="Quality Nexus on Facebook (opens in a new tab)" href="https://www.facebook.com/profile.php?id=61578267442680" target="_blank" rel="noopener noreferrer" className="hover:text-nexus-teal transition-colors">
                <FaFacebook size={20} aria-hidden="true" />
              </a>
              <a aria-label="Quality Nexus on X (opens in a new tab)" href="https://x.com/QualityNexus" target="_blank" rel="noopener noreferrer" className="hover:text-nexus-teal transition-colors">
                <SiX size={20} aria-hidden="true" />
              </a>
              <a aria-label="Quality Nexus on LinkedIn (opens in a new tab)" href="https://www.linkedin.com/company/quality-nexus" target="_blank" rel="noopener noreferrer" className="hover:text-nexus-teal transition-colors">
                <FaLinkedin size={20} aria-hidden="true" />
              </a>
              <a aria-label="Quality Nexus on Medium (opens in a new tab)" href="https://medium.com/qualitynexus" target="_blank" rel="noopener noreferrer" className="hover:text-nexus-teal transition-colors">
                <SiMedium size={20} aria-hidden="true" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold text-lg mb-4 text-nexus-gray dark:text-gray-200">{t("footer.quickLinks.title")}</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/services" className="text-gray-500 dark:text-gray-300 hover:text-nexus-teal transition-colors">
                  {t("footer.quickLinks.services")}
                </Link>
              </li>
              <li>
                <Link to="/community" className="text-gray-500 dark:text-gray-300 hover:text-nexus-teal transition-colors">
                  {t("footer.quickLinks.community")}
                </Link>
              </li>
              <li>
                <Link to="/events" className="text-gray-500 dark:text-gray-300 hover:text-nexus-teal transition-colors">
                  {t("footer.quickLinks.events")}
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-gray-500 dark:text-gray-300 hover:text-nexus-teal transition-colors">
                  {t("footer.quickLinks.blog")}
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-500 dark:text-gray-300 hover:text-nexus-teal transition-colors">
                  {t("footer.quickLinks.contact")}
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-gray-500 dark:text-gray-300 hover:text-nexus-teal transition-colors">
                  {t("footer.quickLinks.about")}
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-semibold text-lg mb-4 text-nexus-gray dark:text-gray-200">{t("footer.services.title")}</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/services/mobile-apps-testing" className="text-gray-500 dark:text-gray-300 hover:text-nexus-teal transition-colors">
                  {t("footer.services.mobileApps")}
                </Link>
              </li>
              <li>
                <Link to="/services/api-testing" className="text-gray-500 dark:text-gray-300 hover:text-nexus-teal transition-colors">
                  {t("footer.services.api")}
                </Link>
              </li>
              <li>
                <Link to="/services/performance-testing" className="text-gray-500 dark:text-gray-300 hover:text-nexus-teal transition-colors">
                  {t("footer.services.performance")}
                </Link>
              </li>
              <li>
                <Link to="/services/security-testing" className="text-gray-500 dark:text-gray-300 hover:text-nexus-teal transition-colors">
                  {t("footer.services.security")}
                </Link>
              </li>
              <li>
                <Link to="/services" className="text-gray-500 dark:text-gray-300 hover:text-nexus-teal transition-colors">
                  {t("footer.services.all")}
                </Link>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h3 className="font-semibold text-lg mb-4 text-nexus-gray dark:text-gray-200">{t("footer.newsletter.title")}</h3>
            <p className="text-gray-500 dark:text-gray-300 mb-4">
              {t("footer.newsletter.description")}
            </p>
            <div className="flex flex-col space-y-3">
              <label htmlFor="footer-newsletter-email" className="sr-only">
                {t("footer.newsletter.emailPlaceholder")}
              </label>
              <Input
                id="footer-newsletter-email"
                type="email"
                placeholder={t("footer.newsletter.emailPlaceholder")}
                className="border-gray-300  focus:border-nexus-teal focus:ring-nexus-teal"
              />
              <Button className="bg-nexus-brand hover:bg-nexus-brand/90 w-full">{t("footer.newsletter.subscribe")}</Button>
            </div>
          </div>
        </div>

        <div className="pt-8 mt-8 border-t border-gray-200 text-gray-500 dark:text-gray-300 text-sm flex flex-col md:flex-row justify-between items-center">
          <p>&copy; {new Date().getFullYear()} {t("footer.copyrights")}</p>
          <div className="flex ltr:space-x-4 rtl:space-x-reverse rtl:space-x-4 ltr:mt-4 rtl:mt-4 md:mt-0">
            <Link to="/privacy-policy" className="hover:text-nexus-teal transition-colors">
              {t("footer.privacy")}
            </Link>
            <Link to="/terms-of-service" className="hover:text-nexus-teal transition-colors ltr:ml-4 rtl:mr-4">
              {t("footer.terms")}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
